import React, { useEffect, useState } from "react";
import "twin.macro";
import "styled-components/macro";
import { IconButton, MenuItem, Tooltip, InputAdornment } from "@mui/material";
import {
  Select,
  TextField,
  Popover,
} from "@madie/madie-design-system/dist/react";
import SearchIcon from "@mui/icons-material/Search";

import ClearIcon from "@mui/icons-material/Clear";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import LibraryAddIcon from "@mui/icons-material/LibraryAdd";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { useFormik } from "formik";
import queryString from "query-string";
import { useNavigate, useLocation } from "react-router-dom";
import { useFeatureFlags } from "@madie/madie-util";
import { blue, grey, red } from "@mui/material/colors";
import { TestCase } from "@madie/madie-models";
import { Icon } from "@iconify-icon/react";

interface ActionCenterProps {
  onSubmit?: any;
  selectedTestCases: any;
  canEdit: boolean;
  isQDM: boolean;
  setDeleteDialogModalOpen?: Function;
  setShiftDatesDialogModalOpen?: Function;
  onCloneTestCase?: (testCase: TestCase) => void;
  exportTestCases?: Function;
  onExportQRDA?: Function;
  onExportExcel?: Function;
  measureId?: string;
  exportOptionsOpen?: boolean;
  setExportOptionsOpen?: Function;
  displayTestCaseCopyDialog?: Function;
  executeAllTestCases?: boolean;
  isDraft?;
}

const filterByOptions = ["Case #", "Status", "Group", "Title", "Description"];

export default function ActionCenter(props: ActionCenterProps) {
  const {
    selectedTestCases,
    canEdit,
    isQDM,
    onCloneTestCase,
    setDeleteDialogModalOpen,
    setShiftDatesDialogModalOpen,
    exportTestCases,
    onExportQRDA,
    onExportExcel,
    measureId,
    exportOptionsOpen,
    setExportOptionsOpen,
    displayTestCaseCopyDialog,
    executeAllTestCases,
    isDraft,
  } = props;

  const [disableDeleteBtn, setDisableDeleteBtn] = useState<boolean>(true);
  const [disableShiftDatesBtn, setDisableShiftDatesBtn] =
    useState<boolean>(true);
  const [disableCloneBtn, setDisableCloneBtn] = useState<boolean>(true);
  const [disableExportBtn, setDisableExportBtn] = useState<boolean>(true);
  const [disableCopyBtn, setDisableCopyBtn] = useState<boolean>(true);
  const [disabledDeleteBtnMessage, setDisabledDeleteBtnMessage] = useState<
    string | undefined
  >();

  useEffect(() => {
    deleteButtonCheck();
    shiftDatesButtonCheck();
    cloneButtonCheck();
    exportButtonCheck();
    copyButtonCheck();
  }, [selectedTestCases, canEdit, isQDM]);

  const { search } = useLocation();
  let navigate = useNavigate();
  const featureFlags = useFeatureFlags();
  const values = queryString.parse(search);

  // init against URL
  const formik = useFormik({
    initialValues: {
      filterBy: values.filter ? values.filter : "",
      searchValue: values.search ? values.search : "",
    },
    enableReinitialize: true,
    onSubmit: async (formValues) => {
      props.onSubmit(formValues);
    },
  });

  const createEncodedQuery = (values) => {
    const filterEncoded = encodeURIComponent(values.filterBy);
    const searchEncoded = encodeURIComponent(values.searchValue);
    return `?filter=${filterEncoded}&search=${searchEncoded}&page=1&limit=${
      values.limit || 10
    }`;
  };

  const handleNavigate = () => {
    navigate(createEncodedQuery(formik.values));
  };

  const handleClearClick = () => {
    const testCasePageOptions = JSON.parse(
      window.localStorage.getItem("testCasesPageOptions")
    );
    localStorage.setItem(
      "testCasesPageOptions",
      JSON.stringify({
        page: 1,
        limit: testCasePageOptions?.limit ? testCasePageOptions.limit : 10,
        filter: "",
        search: "",
      })
    );
    navigate(window.location.pathname);
  };

  const deleteButtonCheck = () => {
    if (canEdit && selectedTestCases?.length >= 1) {
      if (!isDraft) {
        const hasCreatedBeforeVersioning = selectedTestCases.some(
          (testCase) => testCase.createdBeforeVersioning === true
        );
        setDisableDeleteBtn(hasCreatedBeforeVersioning);
        setDisabledDeleteBtnMessage(
          "Test cases added prior to versioning cannot be deleted"
        );
      } else {
        setDisableDeleteBtn(false);
      }
    } else {
      setDisabledDeleteBtnMessage("Select a test case to delete");
      setDisableDeleteBtn(true);
    }
  };

  const shiftDatesButtonCheck = () => {
    if (canEdit && selectedTestCases?.length >= 1) {
      setDisableShiftDatesBtn(false);
    } else {
      setDisableShiftDatesBtn(true);
    }
  };

  const cloneButtonCheck = () => {
    if (
      canEdit &&
      selectedTestCases?.length == 1 &&
      selectedTestCases[0]?.validResource
    ) {
      setDisableCloneBtn(false);
    } else {
      setDisableCloneBtn(true);
    }
  };

  const exportButtonCheck = () => {
    if (isQDM) {
      if (executeAllTestCases) {
        setDisableExportBtn(false);
      } else {
        setDisableExportBtn(true);
      }
    } else {
      if (selectedTestCases?.length > 0) {
        setDisableExportBtn(false);
      } else {
        setDisableExportBtn(true);
      }
    }
  };

  const copyButtonCheck = () => {
    if (selectedTestCases?.length > 0) {
      setDisableCopyBtn(false);
    } else {
      setDisableCopyBtn(true);
    }
  };

  const [anchorEl, setAnchorEl] = useState(null);
  const handleClose = () => {
    setExportOptionsOpen(false);
    setAnchorEl(null);
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <div tw="flex py-4 justify-between items-center">
        <div tw="flex w-1/2 pr-4">
          <div tw="w-1/2 pr-2">
            <Select
              label="Filter By"
              id="filter-by-select"
              data-testid="filter-by-select"
              inputProps={{ "data-testid": "filter-by-select-input" }}
              placeHolder={{ name: "Filter By", value: "" }}
              SelectDisplayProps={{
                "aria-required": "true",
              }}
              size="small"
              name="filterBy"
              value={formik.values.filterBy}
              onChange={formik.handleChange}
              options={filterByOptions
                ?.map((option) => {
                  return (
                    <MenuItem
                      key={option}
                      value={option}
                      data-testid={`filter-by-${option}`}
                    >
                      {option}
                    </MenuItem>
                  );
                })
                .concat(
                  <MenuItem key="-" value="" data-testid={`filter-by--`}>
                    -
                  </MenuItem>
                )}
            />
          </div>
          <div tw="w-1/2 pl-2">
            <TextField
              id="search"
              tw="w-full"
              label="Search"
              placeholder="Search"
              inputProps={{
                "data-testid": "test-case-list-search-input",
              }}
              data-testid="test-case-list-search"
              name="searchValue"
              value={formik.values.searchValue}
              onChange={formik.handleChange}
              onKeyPress={(e) => {
                if (e.key === "Enter") {
                  handleNavigate();
                }
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment
                      position="start"
                      data-testid="test-cases-trigger-search"
                      onClick={handleNavigate}
                      style={{ cursor: "pointer" }}
                    >
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment
                      data-testid="test-cases-clear-search"
                      position="end"
                      style={{ cursor: "pointer" }}
                      onClick={handleClearClick}
                    >
                      <IconButton>
                        <ClearIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </div>
        </div>

        {/* Action Buttons (Delete, Shift Test Case Dates, Clone, Export) */}
        <div tw="flex items-center">
          {canEdit && (
            <div tw="flex items-center">
              <Tooltip
                data-testid="delete-tooltip"
                title={
                  disableDeleteBtn
                    ? disabledDeleteBtnMessage
                    : "Delete test case"
                }
                placement="top"
                arrow
              >
                <span>
                  <IconButton
                    onClick={() => {
                      setDeleteDialogModalOpen(true);
                    }}
                    disabled={disableDeleteBtn}
                    data-testid="delete-action-btn"
                  >
                    <DeleteOutlinedIcon data-testid={`delete-action-icon`} />
                  </IconButton>
                </span>
              </Tooltip>

              {isDraft && (
                <Tooltip
                  data-testid="shift-test-case-dates-tooltip"
                  title={
                    disableShiftDatesBtn
                      ? "Select test cases to shift test case dates"
                      : "Shift test case dates"
                  }
                  placement="top"
                  arrow
                >
                  <span>
                    <IconButton
                      onClick={() => {
                        setShiftDatesDialogModalOpen(true);
                      }}
                      disabled={disableShiftDatesBtn}
                      data-testid="shift-test-case-dates-action-btn"
                    >
                      <EditCalendarOutlinedIcon
                        data-testid={`shift-test-case-dates-action-icon`}
                      />
                    </IconButton>
                  </span>
                </Tooltip>
              )}

              <Tooltip
                data-testid="clone-tooltip"
                title={
                  disableCloneBtn
                    ? "Select a valid test case to clone"
                    : "Clone test case"
                }
                placement="top"
                arrow
              >
                <span>
                  <IconButton
                    onClick={(e) => {
                      onCloneTestCase(selectedTestCases?.[0]);
                    }}
                    disabled={disableCloneBtn}
                    data-testid="clone-action-btn"
                  >
                    <LibraryAddIcon data-testid={`clone-action-icon`} />
                  </IconButton>
                </span>
              </Tooltip>
            </div>
          )}

          <Tooltip
            data-testid="copy-tooltip"
            title={
              disableCopyBtn
                ? "Select test cases to copy to another measure"
                : "Copy to another measure"
            }
            placement="top"
            arrow
          >
            <span>
              <IconButton
                onClick={() => {
                  displayTestCaseCopyDialog();
                }}
                disabled={disableCopyBtn}
                data-testid="copy-action-btn"
              >
                <Icon
                  icon="fluent:share-screen-start-24-regular"
                  data-testid={`copy-action-icon`}
                  rotate={45}
                />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip
            data-testid="export-tooltip"
            title={
              disableExportBtn
                ? isQDM
                  ? executeAllTestCases
                    ? "Select test cases to export"
                    : "Test cases must be executed prior to exporting."
                  : "Select test cases to export"
                : "Export test cases"
            }
            placement="top"
            arrow
          >
            <span>
              <IconButton
                disabled={disableExportBtn}
                data-testid="export-action-btn"
              >
                <FileUploadOutlinedIcon
                  data-testid={`export-action-icon`}
                  onClick={(event) => {
                    event.preventDefault();
                    setAnchorEl(event.currentTarget);
                    setExportOptionsOpen(true);
                  }}
                />
                {!isQDM && (
                  <Popover
                    optionsOpen={exportOptionsOpen}
                    anchorEl={anchorEl}
                    handleClose={handleClose}
                    canEdit={canEdit}
                    additionalSelectOptionProps={[
                      {
                        label: "Transaction Bundle",
                        dataTestId: `export-transaction-bundle`,
                        toImplementFunction: () => {
                          exportTestCases("TRANSACTION");
                        },
                      },
                      {
                        label: "Collection Bundle",
                        dataTestId: `export-collection-bundle`,
                        toImplementFunction: () => {
                          exportTestCases("COLLECTION");
                        },
                      },
                    ]}
                  />
                )}
                {isQDM && (
                  <Popover
                    optionsOpen={exportOptionsOpen}
                    anchorEl={anchorEl}
                    handleClose={handleClose}
                    canEdit={canEdit}
                    additionalSelectOptionProps={[
                      {
                        label: "QRDA",
                        toImplementFunction: onExportQRDA,
                        dataTestId: `export-qrda-${measureId}`,
                      },
                      {
                        label: "Excel",
                        toImplementFunction: onExportExcel,
                        dataTestId: `export-excel-${measureId}`,
                      },
                    ]}
                  />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>
    </form>
  );
}
